import React from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer, LabelList
} from 'recharts';

// Helper function to identify company (ASI) models
const isCompanyModel = (modelId) => {
  return modelId === 'asi1_mini' || modelId === 'asi1_mini_0225';
};

// Desktop Vertical Chart Component
const DesktopVerticalChart = ({ modelColumns, averageScores, isChartCompact }) => {
  return (
    <div style={{ height: isChartCompact ? '18rem' : '22rem' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={modelColumns.map(model => ({
            name: model.name,
            score: Math.round(Number(averageScores[model.id])),
            id: model.id
          }))}
          margin={{ top: 5, right: 30, left: 20, bottom: 70 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="name" 
            angle={-45} 
            textAnchor="end" 
            height={70} 
            tick={{ fontSize: 12 }}
          />
          <YAxis 
            domain={[0, 100]} 
            label={{ value: 'MMLU Score (%)', angle: -90, position: 'insideLeft' }} 
          />
          <Tooltip formatter={(value) => [`${value}%`, 'MMLU Score']} />
          <Bar 
            dataKey="score" 
            fill="#3b82f6" 
            name="Average MMLU Score"
            radius={[4, 4, 0, 0]}
          >
            {/* Add label list for desktop chart */}
            <LabelList 
              dataKey="score" 
              position="top" 
              formatter={(value) => `${value}%`} 
              fill="white"
              style={{ fontWeight: 'bold' }}
              content={(props) => {
                const { x, y, width, height, value, index } = props;
                const isASIModel = isCompanyModel(modelColumns[index].id);
                
                return (
                  <g>
                    <text 
                      x={x + width / 2} 
                      y={isASIModel ? y + height / 2 : y - 10} // For ASI models, place text inside bar
                      textAnchor="middle" 
                      dominantBaseline={isASIModel ? "middle" : "auto"}
                      fill={isASIModel ? "white" : "#1e40af"} // White text for ASI models
                      fontWeight="bold"
                      fontSize={12}
                    >
                      {`${value}%`}
                    </text>
                  </g>
                );
              }}
            />
            {modelColumns.map((model) => (
              <Cell 
                key={`cell-${model.id}`} 
                fill={isCompanyModel(model.id)
                  ? (Number(averageScores[model.id]) >= 90 ? '#b91c1c' : 
                     Number(averageScores[model.id]) >= 85 ? '#dc2626' : 
                     Number(averageScores[model.id]) >= 80 ? '#ef4444' : 
                     Number(averageScores[model.id]) >= 75 ? '#f87171' : '#fca5a5')
                  : (Number(averageScores[model.id]) >= 90 ? '#1e40af' : 
                     Number(averageScores[model.id]) >= 85 ? '#2563eb' : 
                     Number(averageScores[model.id]) >= 80 ? '#3b82f6' : 
                     Number(averageScores[model.id]) >= 75 ? '#60a5fa' : '#93c5fd')} 
                stroke={isCompanyModel(model.id) ? '#991b1b' : undefined}
                strokeWidth={isCompanyModel(model.id) ? 2 : 0}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Mobile Horizontal Chart Component
const MobileHorizontalChart = ({ modelColumns, averageScores, isChartCompact }) => {
  // Function to create compact model names
  const getCompactName = (fullName) => {
    if (fullName.startsWith("LLAMA")) {
      const version = fullName.split(' ')[1];
      return `L${version}`;
    } else if (fullName.startsWith("GPT")) {
      return fullName.split(' ').slice(0, 2).join(' ');
    } else {
      return fullName.split(' ')[0];
    }
  };

  // Sort models by score for better visualization
  const sortedData = [...modelColumns]
    .map(model => ({
      name: getCompactName(model.name),
      score: Math.round(Number(averageScores[model.id])),
      id: model.id
    }))
    .sort((a, b) => b.score - a.score);

  return (
    <div style={{ height: isChartCompact ? '22rem' : '26rem' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={sortedData}
          margin={{ top: 5, right: 5, left: 40, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            type="number" 
            domain={[70, 100]} 
            label={{ value: 'Score (%)', position: 'insideBottom', offset: -5 }}
          />
          <YAxis 
            type="category" 
            dataKey="name" 
            width={40}
            tick={{ fontSize: 10 }}
            textAnchor="end"
          />
          <Tooltip formatter={(value) => [`${value}%`, 'MMLU Score']} />
          <Bar 
            dataKey="score" 
            name="Average MMLU Score"
            radius={[0, 4, 4, 0]}
            barSize={25}
          >
            {sortedData.map((entry) => {
              const isASIModel = isCompanyModel(entry.id);
              return (
                <Cell 
                  key={entry.id}
                  fill={isASIModel
                    ? (entry.score >= 90 ? '#b91c1c' : 
                       entry.score >= 85 ? '#dc2626' : 
                       entry.score >= 80 ? '#ef4444' : 
                       entry.score >= 75 ? '#f87171' : '#fca5a5')
                    : (entry.score >= 90 ? '#1e40af' : 
                       entry.score >= 85 ? '#2563eb' : 
                       entry.score >= 80 ? '#3b82f6' : 
                       entry.score >= 75 ? '#60a5fa' : '#93c5fd')} 
                  stroke={isASIModel ? '#991b1b' : undefined}
                  strokeWidth={isASIModel ? 2 : 0}
                />
              );
            })}
            {/* Display score values inside bars with custom label renderer */}
            <LabelList 
              dataKey="score" 
              position="inside" 
              formatter={(value) => `${value}%`} 
              fill="white" 
              fontSize={12}
              stroke="none"
              strokeWidth={0}
              style={{ fill: 'white', fontWeight: 'bold' }}
              // Using a custom label renderer to ensure white text
              content={(props) => {
                const { x, y, width, height, value, index } = props;
                return (
                  <g>
                    <text 
                      x={x + width / 2} 
                      y={y + height / 2} 
                      textAnchor="middle" 
                      dominantBaseline="middle"
                      fill="white"
                      fontWeight="bold"
                      fontSize={12}
                    >
                      {`${value}%`}
                    </text>
                  </g>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Main SummaryTab Component
const SummaryTab = ({ 
  modelColumns, 
  averageScores, 
  isMobileView, 
  isChartCompact 
}) => {
  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
        <h2 className={`text-lg font-semibold mb-2 ${isMobileView ? 'text-center' : ''}`}>Overall Average MMLU Scores</h2>
        <div className={`grid ${isMobileView ? 'grid-cols-1 gap-3' : 'grid-cols-2 md:grid-cols-4 gap-4'}`}>
          {modelColumns.map(model => {
            const isASIModel = isCompanyModel(model.id);
            return (
              <div 
                key={model.id} 
                className={`p-4 rounded-lg shadow-sm ${
                  isASIModel 
                    ? 'bg-blue-50 border-2 border-blue-400'
                    : 'bg-white border'
                }`}
              >
                <div className={`${isMobileView ? 'text-lg text-center' : 'text-base'} ${
                  isASIModel 
                    ? 'text-blue-800 font-bold'
                    : 'text-gray-600 font-semibold'
                }`}>
                  {isASIModel ? '★ ' : ''}{model.name}{isASIModel ? ' ★' : ''}
                </div>
                <div className={`${isMobileView ? 'text-3xl text-center' : 'text-2xl'} font-bold mt-1 ${
                  isASIModel
                    ? 'text-blue-700'
                    : Number(averageScores[model.id]) >= 85 ? 'text-blue-700' : 'text-blue-500'
                }`}>
                  {Math.round(Number(averageScores[model.id]))}%
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Bar Chart for Average Scores */}
      <div className="bg-gray-50 p-2 md:p-4 rounded-lg shadow-sm">
        <h2 className={`text-lg font-semibold mb-4 ${isMobileView ? 'text-center' : ''}`}>Average MMLU Score Comparison</h2>
        
        {/* Conditional rendering based on device type */}
        {isMobileView ? (
          <MobileHorizontalChart 
            modelColumns={modelColumns} 
            averageScores={averageScores} 
            isChartCompact={isChartCompact}
          />
        ) : (
          <DesktopVerticalChart 
            modelColumns={modelColumns} 
            averageScores={averageScores} 
            isChartCompact={isChartCompact}
          />
        )}
      </div>
    </div>
  );
};

export default SummaryTab;
